import 'reflect-metadata';
import {
  Expose,
  plainToInstance,
  instanceToPlain,
  Type,
} from 'class-transformer';

import { Article } from './Article';
import { StoryVideo } from './StoryVideo';
import { TransactionFlow } from './enums/TransactionFlow';
import { TransactionGroup } from './enums/TransactionGroup';
import { LearningItem } from './LearningsPaginated';

export class HomeDashboard {
  @Expose({ name: 'wallet_balance' })
  walletBalance: string;

  @Expose({ name: 'total_earnings' })
  totalEarnings: string;

  @Expose({ name: 'total_paid' })
  totalPaid: string;

  pv?: string;
  tv?: string;
  cv?: string;

  @Expose({ name: 'package_id' })
  packageId?: string;

  @Expose({ name: 'package_name' })
  packageName?: string;

  @Expose({ name: 'package_amount' })
  packageAmount?: string;

  @Expose({ name: 'package_bv' })
  packageBv?: string;

  @Expose({ name: 'package_expiry' })
  @Type(() => Date)
  packageExpiry?: Date;

  @Expose({ name: 'package_purchase_date' })
  @Type(() => Date)
  packagePurchasedOn?: Date;

  @Expose({ name: 'rank_id' })
  rankId?: number;

  @Expose({ name: 'rank_name' })
  rankName?: string;

  @Expose({ name: 'rank_icon' })
  rankIcon?: string;

  @Expose({ name: 'rank_bonus' })
  rankBonus?: string;

  @Expose({ name: 'rank_achieved_date' })
  @Type(() => Date)
  rankAchievedOn?: Date;

  @Expose({ name: 'is_highest_package' })
  isHighestPackage?: boolean;

  @Expose({ name: 'recent_joinings' })
  @Type(() => HomeJoining)
  recentJoinings?: HomeJoining[];

  @Expose({ name: 'recent_transactions' })
  @Type(() => HomeTransaction)
  recentTransactions?: HomeTransaction[];

  @Expose({ name: 'recent_articles' })
  @Type(() => Article)
  recentArticles?: Article[];

  @Expose({ name: 'recent_videos' })
  @Type(() => StoryVideo)
  recentVideos?: StoryVideo[];

  @Expose({ name: 'transaction_labels' })
  transactionLabels: Record<string, string>;

  @Expose({ name: 'affiliate_products_category' })
  affiliateProductsCategory?: number;

  @Expose({ name: 'credit_history' })
  creditHistory: Record<string, string> | null;

  @Expose({ name: 'debit_history' })
  debitHistory: Record<string, string> | null;

  @Type(() => LearningItem)
  learnings: LearningItem[];

  constructor(
    walletBalance: string,
    totalEarnings: string,
    totalPaid: string,
    transactionLabels: Record<string, string>,
    creditHistory: Record<string, string> | null,
    debitHistory: Record<string, string> | null,
    learnings: LearningItem[],
    pv?: string,
    tv?: string,
    cv?: string,
    packageId?: string,
    packageName?: string,
    packageAmount?: string,
    packageBv?: string,
    packagePurchasedOn?: Date,
    packageExpiry?: Date,
    rankId?: number,
    rankName?: string,
    rankIcon?: string,
    rankBonus?: string,
    rankAchievedOn?: Date,
    isHighestPackage?: boolean,
    recentJoinings?: HomeJoining[],
    recentTransactions?: HomeTransaction[],
    recentArticles?: Article[],
    recentVideos?: StoryVideo[],
    affiliateProductsCategory?: number
  ) {
    this.walletBalance = walletBalance;
    this.totalEarnings = totalEarnings;
    this.totalPaid = totalPaid;
    this.pv = pv;
    this.tv = tv;
    this.cv = cv;
    this.packageId = packageId;
    this.packageName = packageName;
    this.packageAmount = packageAmount;
    this.packageBv = packageBv;
    this.packagePurchasedOn = packagePurchasedOn;
    this.packageExpiry = packageExpiry;
    this.rankId = rankId;
    this.rankName = rankName;
    this.rankIcon = rankIcon;
    this.rankBonus = rankBonus;
    this.rankAchievedOn = rankAchievedOn;
    this.isHighestPackage = isHighestPackage;
    this.recentJoinings = recentJoinings;
    this.recentTransactions = recentTransactions;
    this.recentArticles = recentArticles;
    this.recentVideos = recentVideos;
    this.transactionLabels = transactionLabels;
    this.affiliateProductsCategory = affiliateProductsCategory;
    this.creditHistory = creditHistory;
    this.debitHistory = debitHistory;
    this.learnings = learnings;
  }

  static fromJson(json: Record<string, any>): HomeDashboard {
    return plainToInstance(HomeDashboard, json);
  }

  toJson(): any {
    return instanceToPlain(this);
  }
}

export class HomeJoining {
  id: number;

  @Expose({ name: 'first_name' })
  firstName: string;

  @Expose({ name: 'last_name' })
  lastName?: string;

  avatar?: string;

  @Expose({ name: 'joined_date' })
  @Type(() => Date)
  dateCreated: Date;

  constructor(
    id: number,
    firstName: string,
    dateCreated: Date,
    lastName?: string,
    avatar?: string
  ) {
    this.id = id;
    this.firstName = firstName;
    this.lastName = lastName;
    this.avatar = avatar;
    this.dateCreated = dateCreated;
  }

  static fromJson(json: Record<string, any>): HomeJoining {
    return plainToInstance(HomeJoining, json);
  }

  toJson(): any {
    return instanceToPlain(this);
  }
}

export class HomeTransaction {
  id: number;

  @Expose({ name: 'transaction_group' })
  group: TransactionGroup;

  @Expose({ name: 'transaction_type' })
  type: string;

  @Expose({ name: 'transaction_key' })
  key: string;

  amount: string;

  @Expose({ name: 'cash_flow' })
  flow: TransactionFlow;

  @Expose({ name: 'created_at' })
  @Type(() => Date)
  dateCreated: Date;

  level?: number;

  @Expose({ name: 'from_user' })
  fromUser?: string;

  @Expose({ name: 'rank_name' })
  rankName?: string;

  constructor(
    id: number,
    group: TransactionGroup,
    type: string,
    key: string,
    amount: string,
    flow: TransactionFlow,
    dateCreated: Date,
    level?: number,
    fromUser?: string,
    rankName?: string
  ) {
    this.id = id;
    this.group = group;
    this.type = type;
    this.key = key;
    this.amount = amount;
    this.flow = flow;
    this.dateCreated = dateCreated;
    this.level = level;
    this.fromUser = fromUser;
    this.rankName = rankName;
  }

  static fromJson(json: Record<string, any>): HomeTransaction {
    return plainToInstance(HomeTransaction, json);
  }

  toJson(): any {
    return instanceToPlain(this);
  }
}
